import React from 'react';
import axios from "axios";

import { Container, Grid, useMantineTheme, Box, Group, Flex } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { IconFilter, IconReload, IconCirclePlus } from '@tabler/icons-react';

import { useLoginContext } from "../../context/loginContext";

import { BE_API_ONE, utils } from "../../utils/globalVariables";
import { regexNoStriped } from "../../utils/globalFunctions";

import AmountOfData from "../../components/general/AmountOfData";
import ActionIconComponent from "../../components/general/ActionIconComponent";

import NoData from "../../components/general/NoData";
import WrapperUtils from "../../layout/WrapperUtils";

import DiscountSpecialCard from "./DiscountSpecialCard";
import DiscountSpecialAdd from "./DiscountSpecialAdd";
import DiscountSpecialEdit from "./DiscountSpecialEdit";
import DiscountSpecialDelete from "./DiscountSpecialDelete";
import DiscountSpecialView from "./DiscountSpecialView";
import DiscountSpecialFilter from "./DiscountSpecialFilter";
import SearchInputComponent from "../../components/general/SearchInputComponent";

const DiscountSpecialIndex = () => {

  const mountedRef = React.useRef(true);

  const theme = useMantineTheme();
  const matchesMinMD = useMediaQuery(`(min-width: ${theme.breakpoints.md})`);
  const { loginContext, handleSetLoginContext } = useLoginContext();

  const notificationId = React.useRef({
    errorGetData: 'DiscountSpecialIndex-1',
  })

  const filterDefaultRef = React.useRef({
    isActive: utils.flagAll,
    deductedFrom: utils.flagAll,
    type: utils.flagAll,
  })

  const sortList = {
    name: {
      slug: 'diskon_code_prefix',
      caption: 'Nama',
      type: utils.sortType.string.slug
    },
    dateCreated: {
      slug: 'diskon_date_created',
      caption: 'Created',
      type: utils.sortType.date.slug
    },
  }

  const [data, setData] = React.useState({
    data: [],
    dataCount: 0,
    dataFlag: 'idle',
    dataLoading: false,
    dataRefresh: new Date(),
    dataOffset: 0,
    dataSortSelected: sortList.dateCreated,
    dataSearchInputForBE: '',

    filterData: {
      drawer: false,
      isActive: filterDefaultRef.current.isActive,
      deductedFrom: filterDefaultRef.current.deductedFrom,
      type: filterDefaultRef.current.type,
    },

    addData: {
      drawer: false,
    },

    editData: {
      drawer: false,
      data: []
    },

    deleteData: {
      drawer: false,
      data: []
    },

    viewData: {
      drawer: false,
      data: []
    },
  });

  React.useEffect(() => {
    return () => {
      mountedRef.current = false;
    }
  }, [])

  const fnSort = (stateSortSelected) => {
    setData(d => ({
      ...d,
      dataLoading: true,
      dataSortSelected: {
        ...d.dataSortSelected,
        slug: d.dataSortSelected.slug[0] === '-' ? stateSortSelected.slug : '-' + stateSortSelected.slug,
        caption: stateSortSelected.caption,
        type: stateSortSelected.type
      }
    }));
  };

  const AmountOfDataComponent = () => {
    return (
      <AmountOfData
        stateDataLimit={utils.dataLimit}
        stateDataOffset={data.dataOffset}
        stateAmountDataAll={data.dataCount}
        stateDataFlag={data.dataFlag}
        stateSuffix=''

        fnFirstData={(stateNewOffset) => setData(d => ({
          ...d,
          dataLoading: true,
          dataRefresh: new Date(),
          dataOffset: stateNewOffset
        }))}

        fnLastData={(stateNewOffset) => setData(d => ({
          ...d,
          dataLoading: true,
          dataRefresh: new Date(),
          dataOffset: stateNewOffset
        }))}

        fnPreviousData={(stateNewOffset) => setData(d => ({
          ...d,
          dataLoading: true,
          dataRefresh: new Date(),
          dataOffset: stateNewOffset
        }))}

        fnNextData={(stateNewOffset) => setData(d => ({
          ...d,
          dataLoading: true,
          dataRefresh: new Date(),
          dataOffset: stateNewOffset
        }))}
      />
    )
  }

  React.useEffect(() => {
    setData(d => ({ ...d, dataLoading: true }));

    const formDT = new FormData();
    const formDTHeader = { 'Content-Type': 'application/json' }

    formDT.append('json_token', loginContext.syswebapp);
    formDT.append('data_limit', utils.dataLimit);
    formDT.append('data_offset', data.dataOffset);
    formDT.append('order_by', regexNoStriped(data.dataSortSelected.slug));
    formDT.append('order_type', data.dataSortSelected.slug[0] === '-' ? 'asc' : 'desc');

    formDT.append('flag_all', utils.flagAll);
    formDT.append('filter_search', data.dataSearchInputForBE);

    formDT.append('filter_deducted_from', data.filterData.deductedFrom);
    formDT.append('filter_type', data.filterData.type);
    formDT.append('filter_is_active', data.filterData.isActive);

    formDT.append('discount_flag', utils.discountFlag.discount.value);

    axios({
      method: 'post',
      url: BE_API_ONE + '/discount_special_get',
      data: formDT,
      headers: formDTHeader,
    }).then((response) => {
      if (!mountedRef.current) return null;

      setData(d => ({ ...d, dataLoading: false }));

      if (response.data.flag_status === 'isset') {
        setData(d => ({
          ...d,
          data: response.data.data,
          dataCount: response.data.data_count,
          dataFlag: response.data.data.length > 0 ? true : false,
        }));
      }

      if (response.data.flag_status === 'invalid-json-token') {
        handleSetLoginContext(null);
      }

      if (response.data.flag_status === 'user-inactive') {
        handleSetLoginContext(null);
      }
    }).catch(error => {
      if (!mountedRef.current) return null;

      setData(d => ({
        ...d,
        dataLoading: false,
      }));

      notifications.show({
        id: notificationId.current.errorGetData,
        title: utils.notification.warning.caption,
        message: error.message,
        color: utils.notification.warning.color,
        icon: utils.notification.warning.icon,
        withBorder: true,
        autoClose: true,
      })
    })
  }, [
    handleSetLoginContext,
    loginContext.syswebapp,
    data.dataRefresh,
    data.dataSearchInputForBE,
    data.dataSortSelected.slug,
    data.dataOffset,
    data.filterData.deductedFrom,
    data.filterData.type,
    data.filterData.isActive
  ])

  return (
    <React.Fragment>
      <WrapperUtils stateDataLoading={data.dataLoading}>
        <Group spacing='xs' noWrap>
          <ActionIconComponent
            stateVariant='light'
            stateRadius='xl'
            stateTitle='Filter'
            stateSize='lg'
            fnOnClick={() => setData(d => ({ ...d, filterData: { ...d.filterData, drawer: true } }))}

            StateIcon={IconFilter}
            stateIconSize='1.125rem'
            stateIconStrokeWidth={1.25}
            stateIconStyle={{ color: theme.colorScheme === 'light' ? '#000' : theme.colors.gray[0] }}
          />

          <ActionIconComponent
            stateVariant='light'
            stateRadius='xl'
            stateTitle='Add'
            stateSize='lg'
            fnOnClick={() => setData(d => ({ ...d, addData: { ...d.addData, drawer: true } }))}

            StateIcon={IconCirclePlus}
            stateIconSize='1.25rem'
            stateIconStrokeWidth={1.25}
            stateIconStyle={{ color: theme.colorScheme === 'light' ? '#000' : theme.colors.gray[0] }}
          />

          <ActionIconComponent
            stateVariant='light'
            stateRadius='xl'
            stateTitle='Reload Data'
            stateSize='lg'
            fnOnClick={() => setData(d => ({ ...d, dataLoading: true, dataRefresh: new Date() }))}

            StateIcon={IconReload}
            stateIconSize='1.125rem'
            stateIconStrokeWidth={1.25}
            stateIconStyle={{ color: theme.colorScheme === 'light' ? '#000' : theme.colors.gray[0] }}
          />

          <SearchInputComponent
            statePlaceHolder='Search Diskon'
            fnDoSearch={(val) => setData(d => ({
              ...d,
              dataOffset: 0,
              dataSearchInputForBE: val,
              dataRefresh: new Date(),
            }))}
          />
        </Group>

        {matchesMinMD &&
          <Flex
            sx={{ flex: 1 }}
            justify="flex-end"
            align="center"
            direction="row"
            wrap="nowrap"
          >
            <AmountOfDataComponent />
          </Flex>
        }
      </WrapperUtils>

      <Container fluid>
        <Grid my={0}>
          {data.data.length <= 0 && data.dataFlag !== 'idle' &&
            <Grid.Col span={12}>
              <NoData />
            </Grid.Col>
          }

          {data.data.length > 0 &&
            <Grid.Col
              span={12}
              py={0}
            >
              <Box mt='md'>
                <DiscountSpecialCard
                  stateData={data.data}
                  stateSortList={sortList}
                  stateSortSelected={data.dataSortSelected}
                  fnSort={(val) => {
                    fnSort(val);
                  }}

                  fnSelectedDataView={(val) => setData(d => ({
                    ...d,
                    viewData: {
                      ...d.viewData,
                      data: val,
                      drawer: true
                    }
                  }))}

                  fnSelectedDataEdit={(val) => setData(d => ({
                    ...d,
                    editData: {
                      ...d.editData,
                      data: val,
                      drawer: true
                    }
                  }))}

                  fnSelectedDataDelete={(val) => setData(d => ({
                    ...d,
                    deleteData: {
                      ...d.deleteData,
                      data: val,
                      drawer: true
                    }
                  }))}
                />
              </Box>
            </Grid.Col>
          }

          {data.data.length > 0 &&
            <Grid.Col span={12} className='amountOfDataBottom'>
              <Group mt='xs' mb='md' position='apart'>
                <Box>&nbsp;</Box>

                <AmountOfDataComponent />
              </Group>
            </Grid.Col>
          }
        </Grid>
      </Container>

      <DiscountSpecialFilter
        stateShow={data.filterData.drawer}

        fnShow={() => setData(d => ({ ...d, filterData: { ...d.filterData, drawer: false } }))}

        stateDefaultFilter={{
          ...filterDefaultRef.current,
        }}

        stateFilter={{
          isActive: data.filterData.isActive,
          deductedFrom: data.filterData.deductedFrom,
          type: data.filterData.type,
        }}

        fnFilter={(obj) => setData(d => ({
          ...d,
          filterData: {
            ...d.filterData,
            isActive: obj.isActive,
            deductedFrom: obj.deductedFrom,
            type: obj.type,

            drawer: false
          },

          dataRefresh: new Date(),
          dataLoading: true,
        }))}
      />

      <DiscountSpecialAdd
        stateShow={data.addData.drawer}
        fnShow={() => setData(d => ({ ...d, addData: { ...d.addData, drawer: false } }))}

        fnSuccessAdd={() => {
          setData(d => ({
            ...d,
            addData: { ...d.addData, drawer: false },
            dataRefresh: new Date(),
            dataLoading: true
          }))
        }}
      />

      <DiscountSpecialEdit
        stateShow={data.editData.drawer}
        fnShow={() => setData(d => ({
          ...d,
          editData: {
            ...d.editData,
            drawer: false
          }
        }))}

        stateData={data.editData.data}

        fnSuccessEdit={() => {
          setData(d => ({
            ...d,
            editData: {
              ...d.editData,
              drawer: false
            },
            dataRefresh: new Date(),
            dataLoading: true,
          }))
        }}
      />

      <DiscountSpecialDelete
        stateShow={data.deleteData.drawer}
        fnShow={() => setData(d => ({ ...d, deleteData: { ...d.deleteData, drawer: false } }))}

        stateData={data.deleteData.data}
        fnSuccessDelete={() => {
          setData(d => ({
            ...d,
            deleteData: { ...d.deleteData, drawer: false },
            dataRefresh: new Date(),
            dataLoading: true,
          }))
        }}
      />

      <DiscountSpecialView
        stateShow={data.viewData.drawer}
        fnShow={() => setData(d => ({
          ...d,
          viewData: {
            ...d.viewData,
            drawer: false
          }
        }))}

        stateData={data.viewData.data}
      />
    </React.Fragment>
  )
}

export default DiscountSpecialIndex;